import { useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Center,
  Group,
  Modal,
  NumberInput,
  Select,
  Skeleton,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminErrText, adminGet, adminSend, AdminApiError } from "../../api.ts";
import { AdminTable } from "../../components/AdminTable.tsx";
import { TablePagination } from "../../components/TablePagination.tsx";
import { useClientPagination } from "../../hooks/useClientPagination.ts";

interface ChannelRow {
  id: string;
  purpose: "broadcast" | "support" | "admin_notify" | "referral";
  telegram_chat_id: number;
  chat_type: string | null;
  title: string | null;
  is_enabled: boolean;
  display_order: number;
}

// 仅保留已消费的用途。support 与 bot.links.support_handle 重复，referral 无定义行为，
// 不再开放配置（历史数据若有仍会展示，标注为已弃用）。
const PURPOSE_OPTIONS = [
  { label: "广播 broadcast", value: "broadcast" },
  { label: "管理通知 admin_notify", value: "admin_notify" },
];

const PURPOSE_DESC: Record<string, string> = {
  broadcast: "已生效：jackpot 中奖播报到该频道",
  admin_notify: "已生效：派彩需人工复核等运营告警推送到该频道",
};

export function ChannelsTab(props: { readonly botId: string }) {
  const { botId } = props;
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [purposeFilter, setPurposeFilter] = useState<"all" | ChannelRow["purpose"]>("all");
  const [enabledFilter, setEnabledFilter] = useState<"all" | "on" | "off">("all");
  const [search, setSearch] = useState("");

  const form = useForm<{ purpose: string; telegramChatId: number | string }>({
    initialValues: { purpose: "broadcast", telegramChatId: "" },
    validate: {
      purpose: (v) => (v === "" ? "请选择用途" : null),
      telegramChatId: (v) => (v === "" ? "请填写 Chat ID" : null),
    },
  });

  const list = useQuery({
    queryKey: ["bot", botId, "channels"],
    queryFn: () => adminGet<{ items: ChannelRow[] }>(`/bots/${botId}/channels`),
    refetchInterval: 60_000,
  });
  const create = useMutation({
    mutationFn: (v: { purpose: string; telegramChatId: number }) =>
      adminSend("POST", `/bots/${botId}/channels`, v),
    onSuccess: () => {
      notifications.show({ color: "green", message: "已添加" });
      setCreateOpen(false);
      form.reset();
      void qc.invalidateQueries({ queryKey: ["bot", botId, "channels"] });
    },
    onError: (e) =>
      notifications.show({ color: "red", message: adminErrText((e as AdminApiError).code) }),
  });
  const patch = useMutation({
    mutationFn: (args: { id: string; isEnabled?: boolean; displayOrder?: number }) =>
      adminSend("PATCH", `/bots/${botId}/channels/${args.id}`, {
        ...(args.isEnabled !== undefined ? { isEnabled: args.isEnabled } : {}),
        ...(args.displayOrder !== undefined ? { displayOrder: args.displayOrder } : {}),
      }),
    onSuccess: () => {
      notifications.show({ color: "green", message: "已更新" });
      void qc.invalidateQueries({ queryKey: ["bot", botId, "channels"] });
    },
    onError: (e) =>
      notifications.show({ color: "red", message: adminErrText((e as AdminApiError).code) }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => adminSend("DELETE", `/bots/${botId}/channels/${id}`),
    onSuccess: () => {
      notifications.show({ color: "green", message: "已删除" });
      void qc.invalidateQueries({ queryKey: ["bot", botId, "channels"] });
    },
    onError: (e) =>
      notifications.show({ color: "red", message: adminErrText((e as AdminApiError).code) }),
  });

  const allItems = list.data?.items ?? [];
  const keyword = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    return allItems.filter((row) => {
      if (purposeFilter !== "all" && row.purpose !== purposeFilter) return false;
      if (enabledFilter === "on" && !row.is_enabled) return false;
      if (enabledFilter === "off" && row.is_enabled) return false;
      if (keyword === "") return true;
      const title = (row.title ?? "").toLowerCase();
      const chatId = String(row.telegram_chat_id);
      return title.includes(keyword) || chatId.includes(keyword);
    });
  }, [allItems, purposeFilter, enabledFilter, keyword]);
  const resetKey = `${purposeFilter}|${enabledFilter}|${keyword}`;
  const { pageItems, page, setPage, pageSize, total, totalPages } = useClientPagination(
    filtered,
    resetKey,
  );

  const rows = pageItems.map((row) => (
    <Table.Tr key={row.id}>
      <Table.Td>
        <Tooltip label={PURPOSE_DESC[row.purpose] ?? "该用途已弃用，当前未消费"} withArrow>
          <Badge color={PURPOSE_DESC[row.purpose] !== undefined ? "blue" : "gray"}>
            {row.purpose}
          </Badge>
        </Tooltip>
      </Table.Td>
      <Table.Td>{row.title ?? "-"}</Table.Td>
      <Table.Td>{row.telegram_chat_id}</Table.Td>
      <Table.Td>{row.chat_type ?? "-"}</Table.Td>
      <Table.Td>
        <NumberInput
          size="xs"
          style={{ width: 70 }}
          value={row.display_order}
          min={0}
          onBlur={(e) => {
            const val = Number(e.currentTarget.value);
            if (val !== row.display_order) {
              patch.mutate({ id: row.id, displayOrder: val });
            }
          }}
        />
      </Table.Td>
      <Table.Td>
        <Switch
          checked={row.is_enabled}
          onChange={(e) => patch.mutate({ id: row.id, isEnabled: e.currentTarget.checked })}
        />
      </Table.Td>
      <Table.Td>
        <Button
          size="xs"
          color="red"
          variant="outline"
          onClick={() => {
            modals.openConfirmModal({
              title: "确认删除",
              children: `确认删除频道「${row.title ?? String(row.telegram_chat_id)}」？`,
              labels: { confirm: "删除", cancel: "取消" },
              confirmProps: { color: "red" },
              onConfirm: () => remove.mutate(row.id),
            });
          }}
        >
          删除
        </Button>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <>
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group justify="space-between" mb="md">
          <Text fw={600} size="lg">
            频道
          </Text>
          <Button onClick={() => setCreateOpen(true)}>添加频道</Button>
        </Group>
        {list.isLoading ? (
          <Stack gap="xs">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} h={40} radius="sm" />
            ))}
          </Stack>
        ) : allItems.length === 0 ? (
          <Center py="xl">
            <Stack align="center" gap="sm">
              <Text size="md" fw={600} c="dimmed">
                暂无频道
              </Text>
              <Text size="sm" c="dimmed">
                添加频道后 Bot 可自动推送中奖通知到对应群组或频道。
              </Text>
              <Button onClick={() => setCreateOpen(true)}>立即添加</Button>
            </Stack>
          </Center>
        ) : (
          <>
            <Group gap="sm" mb="sm" wrap="wrap">
              <Select
                size="xs"
                label="用途"
                data={[
                  { label: "全部", value: "all" },
                  { label: "广播 broadcast", value: "broadcast" },
                  { label: "管理通知 admin_notify", value: "admin_notify" },
                  { label: "support（已弃用）", value: "support" },
                  { label: "referral（已弃用）", value: "referral" },
                ]}
                value={purposeFilter}
                onChange={(v) => setPurposeFilter((v ?? "all") as "all" | ChannelRow["purpose"])}
                style={{ width: 180 }}
                clearable={false}
              />
              <Select
                size="xs"
                label="启用"
                data={[
                  { label: "全部", value: "all" },
                  { label: "启用", value: "on" },
                  { label: "禁用", value: "off" },
                ]}
                value={enabledFilter}
                onChange={(v) => setEnabledFilter((v ?? "all") as "all" | "on" | "off")}
                style={{ width: 120 }}
                clearable={false}
              />
              <TextInput
                size="xs"
                label="搜索"
                placeholder="Chat ID / 名称"
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                style={{ minWidth: 200, flex: 1 }}
              />
            </Group>
            <AdminTable minWidth={600}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>用途</Table.Th>
                  <Table.Th>Telegram 名称</Table.Th>
                  <Table.Th>Chat ID</Table.Th>
                  <Table.Th>类型</Table.Th>
                  <Table.Th>顺序</Table.Th>
                  <Table.Th>启用</Table.Th>
                  <Table.Th>操作</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {pageItems.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={7}>
                      <Text c="dimmed" size="sm" ta="center">
                        无匹配频道
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  rows
                )}
              </Table.Tbody>
            </AdminTable>
            <TablePagination
              page={page}
              pageSize={pageSize}
              total={total}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </>
        )}
      </Card>

      <Modal
        title="添加频道"
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        keepMounted={false}
      >
        <form
          onSubmit={form.onSubmit((v) =>
            create.mutate({
              purpose: v.purpose,
              telegramChatId: Number(v.telegramChatId),
            }),
          )}
        >
          <Stack gap="sm">
            <Select
              label="用途"
              data={PURPOSE_OPTIONS}
              required
              {...form.getInputProps("purpose")}
            />
            <NumberInput
              label="Telegram Chat ID"
              description="频道/群组通常为负数。先在 Telegram 把 Bot 加进该会话并设为管理员。"
              required
              {...form.getInputProps("telegramChatId")}
            />
            <Button type="submit" loading={create.isPending}>
              校验并添加
            </Button>
          </Stack>
        </form>
      </Modal>
    </>
  );
}
